import React, { useState, useEffect } from 'react';
import {
  TrendingUp, ScanLine, Activity, BarChart2,
  Calculator, Shield, ArrowRight, X, Clock, Sparkles,
} from 'lucide-react';

// ─── The 7 moments of one complete trade ─────────────────────────────────────
const MOMENTS = [
  {
    id: 'm0',
    time: '8:45 AM',
    phase: 'Pre-Market',
    color: '#6c63ff',
    icon: TrendingUp,
    label: 'Global Cues',
    title: 'The edge is built before market opens',
    situation: 'SGX Nifty up 0.3%. US markets closed green. FII net bought ₹800 Cr yesterday. IT sector showing strength in pre-market futures.',
    move: 'Open Global Cues. Mild gap-up, macro positive. Decide: today is a buy-side day. No stock yet — just context locked in.',
    lesson: 'Traders who skip this are trading blind. 5 minutes here decides your entire day\'s bias.',
    cta: 'Open Global Cues',
    path: '/global-cues',
  },
  {
    id: 'm1',
    time: '9:05 AM',
    phase: 'Pre-Market',
    color: '#4ecca3',
    icon: ScanLine,
    label: 'Scanners',
    title: 'The stock finds you — you don\'t hunt it',
    situation: 'Scanner hits: INFY approaching a 6-month resistance at ₹1,820. Volume building at 2.8× average before market even opens.',
    move: 'Mark ₹1,820 on the chart. Set a price alert. Watchlist is 1 stock deep — not 20. One clean setup.',
    lesson: 'Most traders have 20 stocks and trade none well. One focused setup beats ten half-baked ones every time.',
    cta: 'Open Scanners',
    path: '/analytics',
  },
  {
    id: 'm2',
    time: '9:30 AM',
    phase: 'Market Open',
    color: '#f59e0b',
    icon: Activity,
    label: 'Watch & Wait',
    title: 'The open is the trap — patience is the edge',
    situation: 'Nifty gaps up 0.2%. INFY opens at ₹1,812 moving toward ₹1,820. Everything looks bullish. The temptation to enter is real.',
    move: 'You do nothing. Watch the first 15 minutes from the sidelines. Run the Playbook — lock in global context, gap type, and open behaviour before placing any order.',
    lesson: 'The first 15 minutes are the noisiest of the day. Patience here costs nothing.',
    cta: 'Open Markets',
    path: '/markets',
    altCta: 'Run Playbook',
    altPath: '/appguide?tab=journey',
  },
  {
    id: 'm3',
    time: '9:48 AM',
    phase: 'Setup Forms',
    color: '#4ecca3',
    icon: BarChart2,
    label: 'Read the Chart',
    title: 'Breakout entry is risky — retest entry is disciplined',
    situation: 'INFY breaks ₹1,820 with a strong green candle. Volume spikes to 4× average. Price alert fires.',
    move: 'Don\'t chase the breakout candle. Mark it as a Breakout Setup in the Playbook. Wait for the retest — price pulling back to ₹1,820 to confirm it flipped from resistance to support.',
    lesson: 'The breakout candle traps impatient buyers. The retest separates the real move from the fake one.',
    cta: 'Open Charts',
    path: '/markets',
    altCta: 'Identify Setup in Playbook',
    altPath: '/appguide?tab=journey',
  },
  {
    id: 'm4',
    time: '10:05 AM',
    phase: 'Entry',
    color: '#f97316',
    icon: Calculator,
    label: 'Size & Enter',
    title: 'Position size is math, not feel',
    situation: 'INFY pulls back to ₹1,820, holds with a bullish rejection candle. Retest confirmed. Playbook grade: B.',
    move: 'Risk Calculator: ₹50K capital. SL at ₹1,793 (₹30 below). Risk 1% = ₹500 ÷ ₹30 = 16 shares. Buy 16 INFY @ ₹1,823. Reason: "Retest of 6-month breakout, 4× volume, bullish rejection candle."',
    lesson: 'The reason field is not a formality. If you can\'t write it in one sentence, the trade isn\'t ready.',
    cta: 'Risk Calculator',
    path: '/calculator',
    altCta: 'Place Trade',
    altPath: '/trade',
  },
  {
    id: 'm5',
    time: '12:20 PM',
    phase: 'Exit',
    color: '#16C784',
    icon: TrendingUp,
    label: 'Exit at Plan',
    title: 'Planned exit, executed — not hoped for',
    situation: 'INFY reaches ₹1,882. Target was ₹1,883. Trade ran almost exactly as planned. P&L: +₹928.',
    move: 'Exit fully at ₹1,881. Position closed. Don\'t hold hoping for ₹1,920. The plan said ₹1,883 — honour it.',
    lesson: 'Exiting at plan is a skill. Most traders hold winners too long and give back ₹300 chasing an extra ₹50.',
    cta: 'View Orders',
    path: '/orders',
  },
  {
    id: 'm6',
    time: '4:00 PM',
    phase: 'Review',
    color: '#ce93d8',
    icon: Shield,
    label: 'Review',
    title: 'The review closes the loop — this is how habits form',
    situation: 'Market closed. P&L is green. Most traders have already forgotten the trade and moved on to tomorrow.',
    move: 'Open Trade History. Re-read the reason you wrote at entry. Check Discipline Score. Write one line: "Waited for the retest. Sized by math. Exited at plan. Repeat."',
    lesson: 'One reviewed trade teaches more than ten ignored ones. The review is where a good trade becomes a repeatable system.',
    cta: 'Trade History',
    path: '/trade-history',
  },
];

// ─── Layout constants ─────────────────────────────────────────────────────────
const CANVAS_W = 320;
const NODE_D   = 60;
const ROW_H    = 150;
const PAD_TOP  = 90;
const PAD_BOT  = 80;
const CANVAS_H = PAD_TOP + (MOMENTS.length - 1) * ROW_H + PAD_BOT;

// Winding S-curve column positions
const COL_X = [
  CANVAS_W * 0.50,   // center  0
  CANVAS_W * 0.78,   // right   1
  CANVAS_W * 0.22,   // left    2
  CANVAS_W * 0.78,   // right   3
  CANVAS_W * 0.50,   // center  4
  CANVAS_W * 0.22,   // left    5
  CANVAS_W * 0.50,   // center  6
];

const positions = MOMENTS.map((_, i) => ({
  x: Math.round(COL_X[i]),
  y: PAD_TOP + i * ROW_H,
}));

function segmentD(p1, p2) {
  const midY = (p1.y + p2.y) / 2;
  return `M ${p1.x} ${p1.y} C ${p1.x} ${midY}, ${p2.x} ${midY}, ${p2.x} ${p2.y}`;
}

// ─── Background decoration ────────────────────────────────────────────────────
// Glow blobs placed at the midpoint of each phase pair
const BLOBS = [
  { color: '#6c63ff', cx: CANVAS_W / 2, cy: 165,  rx: 150, ry: 120 }, // pre-market
  { color: '#f59e0b', cx: CANVAS_W / 2, cy: 490,  rx: 150, ry: 130 }, // open/setup
  { color: '#f97316', cx: CANVAS_W / 2, cy: 790,  rx: 140, ry: 120 }, // entry/exit
  { color: '#ce93d8', cx: CANVAS_W / 2, cy: 1040, rx: 130, ry: 100 }, // review
];

const DOTS = [
  { x: 26,  y: 130, r: 2,   color: '#6c63ff', op: 0.35 },
  { x: 298, y: 110, r: 1.5, color: '#6c63ff', op: 0.28 },
  { x: 42,  y: 260, r: 2.5, color: '#4ecca3', op: 0.25 },
  { x: 292, y: 310, r: 1.5, color: '#4ecca3', op: 0.3  },
  { x: 18,  y: 440, r: 2,   color: '#f59e0b', op: 0.28 },
  { x: 304, y: 480, r: 2.5, color: '#f59e0b', op: 0.22 },
  { x: 55,  y: 600, r: 1.5, color: '#4ecca3', op: 0.3  },
  { x: 285, y: 650, r: 2,   color: '#4ecca3', op: 0.25 },
  { x: 20,  y: 760, r: 2.5, color: '#f97316', op: 0.25 },
  { x: 300, y: 800, r: 1.5, color: '#f97316', op: 0.3  },
  { x: 50,  y: 910, r: 2,   color: '#16C784', op: 0.28 },
  { x: 290, y: 960, r: 2,   color: '#16C784', op: 0.22 },
  { x: 25,  y: 1020, r: 1.5, color: '#ce93d8', op: 0.3 },
  { x: 296, y: 1060, r: 2.5, color: '#ce93d8', op: 0.22 },
];

// ─── Popup ────────────────────────────────────────────────────────────────────
function MomentPopup({ moment, pos, idx, colors, onClose, navigate }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  const POPUP_W  = 268;
  const showBelow = pos.y < 420;
  const topVal   = showBelow ? pos.y + NODE_D / 2 + 14 : pos.y - 18;
  const leftVal  = Math.max(4, Math.min(CANVAS_W - POPUP_W - 4, pos.x - POPUP_W / 2));

  return (
    <div
      style={{
        position: 'absolute',
        left: leftVal,
        top: topVal,
        transform: showBelow ? 'translateY(0)' : 'translateY(-100%)',
        width: POPUP_W,
        zIndex: 20,
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.18s ease',
        transformOrigin: showBelow ? 'top center' : 'bottom center',
      }}
    >
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          background: colors.cardBg || '#1a1d2e',
          border: `1.5px solid ${moment.color}55`,
          boxShadow: `0 8px 36px ${moment.color}35, 0 2px 10px rgba(0,0,0,0.5)`,
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-3 py-2.5"
          style={{ background: `${moment.color}14`, borderBottom: `1px solid ${moment.color}25` }}
        >
          <div className="flex items-center gap-2">
            <span
              className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full"
              style={{ background: `${moment.color}25`, color: moment.color, border: `1px solid ${moment.color}40` }}
            >
              {moment.phase}
            </span>
            <span className="flex items-center gap-1 text-[10px] font-bold" style={{ color: 'rgba(255,255,255,0.45)' }}>
              <Clock size={9} />
              {moment.time}
            </span>
          </div>
          <button
            onClick={onClose}
            className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(255,255,255,0.08)' }}
          >
            <X size={10} color="rgba(255,255,255,0.5)" />
          </button>
        </div>

        {/* Body */}
        <div className="p-3 flex flex-col gap-2.5">
          {/* Title */}
          <p className="text-[13px] font-black leading-snug" style={{ color: '#eceef5' }}>
            {moment.title}
          </p>

          {/* Situation */}
          <div
            className="rounded-xl p-2.5"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            <p className="text-[9px] font-black uppercase tracking-widest mb-1" style={{ color: 'rgba(255,255,255,0.35)' }}>
              The Situation
            </p>
            <p className="text-[11px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
              {moment.situation}
            </p>
          </div>

          {/* Your Move */}
          <div
            className="rounded-xl p-2.5"
            style={{ background: `${moment.color}0d`, border: `1px solid ${moment.color}28` }}
          >
            <p className="text-[9px] font-black uppercase tracking-widest mb-1" style={{ color: moment.color }}>
              Your Move
            </p>
            <p className="text-[11px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
              {moment.move}
            </p>
          </div>

          {/* Lesson */}
          <p
            className="text-[11px] leading-relaxed italic pl-2.5"
            style={{ color: 'rgba(255,255,255,0.45)', borderLeft: `2px solid ${moment.color}55` }}
          >
            {moment.lesson}
          </p>

          {/* CTAs */}
          <div className="flex flex-col gap-1.5 pt-0.5">
            <button
              onClick={() => { navigate(moment.path); onClose(); }}
              className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-[12px] font-bold"
              style={{ background: `linear-gradient(135deg, ${moment.color}, ${moment.color}bb)`, color: '#fff' }}
            >
              {moment.cta} <ArrowRight size={12} />
            </button>
            {moment.altCta && (
              <button
                onClick={() => { navigate(moment.altPath); onClose(); }}
                className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-[11px] font-bold"
                style={{ background: `${moment.color}18`, color: moment.color, border: `1px solid ${moment.color}35` }}
              >
                <Sparkles size={11} /> {moment.altCta}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function TradeJourneyMap({ colors, navigate }) {
  const [activeId, setActiveId] = useState(null);
  const activeIdx  = MOMENTS.findIndex(m => m.id === activeId);
  const activeMoment = activeIdx !== -1 ? MOMENTS[activeIdx] : null;

  return (
    <div className="flex flex-col gap-3">

      {/* Header */}
      <div
        className="rounded-2xl p-4"
        style={{
          background: 'linear-gradient(135deg, #6c63ff1a 0%, #f59e0b0d 100%)',
          border: '1px solid rgba(108,99,255,0.25)',
        }}
      >
        <p className="text-[11px] font-black uppercase tracking-widest mb-1" style={{ color: '#6c63ff' }}>
          One Trade · Start to Finish
        </p>
        <p className="text-[17px] font-black leading-tight mb-1.5" style={{ color: '#eceef5', letterSpacing: '-0.02em' }}>
          What a real trading day looks like
        </p>
        <p className="text-[12px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.45)' }}>
          7 moments. One complete trade — from pre-market context to evening review.
          Tap any node to see exactly what to do.
        </p>
      </div>

      {/* Map canvas */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          borderRadius: 20,
          overflow: 'hidden',
          background: 'linear-gradient(180deg, #0c0d1c 0%, #090a12 50%, #0c0d1c 100%)',
          border: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: CANVAS_W,
            maxWidth: '100%',
            margin: '0 auto',
            height: CANVAS_H,
          }}
        >

          {/* ── SVG layer ── */}
          <svg
            viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
            preserveAspectRatio="xMidYMin meet"
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
          >
            <defs>
              <filter id="tj-glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="4" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id="tj-halo" x="-80%" y="-80%" width="260%" height="260%">
                <feGaussianBlur stdDeviation="9" />
              </filter>
              {BLOBS.map((b, i) => (
                <radialGradient key={i} id={`tj-blob-${i}`} cx="50%" cy="50%" r="50%">
                  <stop offset="0%"   stopColor={b.color} stopOpacity="0.13" />
                  <stop offset="100%" stopColor={b.color} stopOpacity="0"    />
                </radialGradient>
              ))}
            </defs>

            {/* Phase glow blobs */}
            {BLOBS.map((b, i) => (
              <ellipse
                key={i}
                cx={b.cx} cy={b.cy}
                rx={b.rx} ry={b.ry}
                fill={`url(#tj-blob-${i})`}
              />
            ))}

            {/* Decorative star dots */}
            {DOTS.map((d, i) => (
              <circle key={i} cx={d.x} cy={d.y} r={d.r} fill={d.color} opacity={d.op} />
            ))}

            {/* 3-layer neon path per segment */}
            {positions.slice(0, -1).map((p, i) => {
              const d     = segmentD(p, positions[i + 1]);
              const color = MOMENTS[i].color;
              return (
                <g key={i}>
                  <path d={d} fill="none" stroke={color} strokeWidth="14" strokeLinecap="round" opacity="0.07" filter="url(#tj-halo)" />
                  <path d={d} fill="none" stroke={color} strokeWidth="6"  strokeLinecap="round" opacity="0.18" filter="url(#tj-glow)" />
                  <path d={d} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" opacity="0.78" />
                </g>
              );
            })}
          </svg>

          {/* ── Node layer ── */}
          {MOMENTS.map((moment, i) => {
            const pos      = positions[i];
            const Icon     = moment.icon;
            const isActive = moment.id === activeId;
            // Time label: alternate above/below to avoid overlap with path
            const timeSide = i % 2 === 0 ? 'left' : 'right';

            return (
              <React.Fragment key={moment.id}>
                {/* Outer ring — always visible */}
                <div
                  style={{
                    position: 'absolute',
                    left: pos.x - NODE_D / 2 - 8,
                    top:  pos.y - NODE_D / 2 - 8,
                    width:  NODE_D + 16,
                    height: NODE_D + 16,
                    borderRadius: '50%',
                    border: `1.5px solid ${moment.color}${isActive ? '80' : '25'}`,
                    zIndex: 2,
                    transition: 'border-color 0.25s',
                    pointerEvents: 'none',
                  }}
                />

                {/* Pulse ring on active */}
                {isActive && (
                  <div
                    className="animate-ping"
                    style={{
                      position: 'absolute',
                      left: pos.x - NODE_D / 2 - 14,
                      top:  pos.y - NODE_D / 2 - 14,
                      width:  NODE_D + 28,
                      height: NODE_D + 28,
                      borderRadius: '50%',
                      background: `${moment.color}1a`,
                      zIndex: 1,
                    }}
                  />
                )}

                {/* Circle */}
                <div
                  role="button"
                  onClick={() => setActiveId(isActive ? null : moment.id)}
                  style={{
                    position: 'absolute',
                    left: pos.x - NODE_D / 2,
                    top:  pos.y - NODE_D / 2,
                    width:  NODE_D,
                    height: NODE_D,
                    borderRadius: '50%',
                    background: isActive
                      ? `linear-gradient(135deg, ${moment.color}, ${moment.color}99)`
                      : `linear-gradient(135deg, ${moment.color}28, ${moment.color}10)`,
                    border: `2px solid ${isActive ? moment.color : moment.color + '45'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    zIndex: 3,
                    transition: 'all 0.22s ease',
                    transform: isActive ? 'scale(1.1)' : 'scale(1)',
                    boxShadow: isActive
                      ? `0 0 0 3px ${moment.color}20, 0 6px 24px ${moment.color}60`
                      : `0 2px 12px ${moment.color}22`,
                  }}
                >
                  <Icon size={22} color={isActive ? '#fff' : moment.color} />
                </div>

                {/* Time badge — alternates left/right to avoid path collision */}
                <div
                  style={{
                    position: 'absolute',
                    top: pos.y - 11,
                    ...(timeSide === 'left'
                      ? { right: CANVAS_W - pos.x + NODE_D / 2 + 10 }
                      : { left: pos.x + NODE_D / 2 + 10 }),
                    zIndex: 3,
                    pointerEvents: 'none',
                  }}
                >
                  <div
                    className="flex items-center gap-1 px-2 py-1 rounded-full"
                    style={{
                      background: `${moment.color}18`,
                      border: `1px solid ${moment.color}35`,
                    }}
                  >
                    <Clock size={8} color={moment.color} />
                    <span className="text-[9px] font-black" style={{ color: moment.color }}>
                      {moment.time}
                    </span>
                  </div>
                </div>

                {/* Label below node */}
                <p
                  style={{
                    position: 'absolute',
                    top: pos.y + NODE_D / 2 + 8,
                    left: pos.x,
                    transform: 'translateX(-50%)',
                    whiteSpace: 'nowrap',
                    fontSize: 10,
                    fontWeight: 800,
                    color: isActive ? moment.color : 'rgba(255,255,255,0.4)',
                    zIndex: 3,
                    transition: 'color 0.22s',
                    pointerEvents: 'none',
                  }}
                >
                  {moment.label}
                </p>
              </React.Fragment>
            );
          })}

          {/* ── Popup ── */}
          {activeMoment && (
            <MomentPopup
              moment={activeMoment}
              pos={positions[activeIdx]}
              idx={activeIdx}
              colors={colors}
              onClose={() => setActiveId(null)}
              navigate={navigate}
            />
          )}

        </div>
      </div>
    </div>
  );
}
